import { useCallback, useContext } from 'react';
import type { GooglePayConfiguration } from '@adyen/adyen-web';
import { CheckoutContext } from '@/context/CheckoutContext';
import { buildGooglePayTaxTransactionInfo } from '@/utils/adyen';
import { shouldUseWalletDerivedZipTax } from '@/utils/tax';
import { logError, logWarn } from '@/utils/logger';
import { ErrorLogs, InfoLogs } from '@/constants/logs';
import { WalletZipTaxStatus } from '@/types/walletZipTax';
import { useWalletZipTaxCalculator } from './useWalletZipTaxCalculator';

// Google kills the sheet with CALLBACK_TIMED_OUT if `onPaymentDataChanged`
// doesn't resolve within ~30s. Warn shortly before that budget so a slow /
// hung tax lookup is attributable from RUM (log-only, does not alter the
// resolve).
const ZIP_TAX_SLOW_WARN_MS = 25_000;

type OnPaymentDataChanged = NonNullable<GooglePayConfiguration['paymentDataCallbacks']>['onPaymentDataChanged'];

/**
 * ACDEV-25067 (Google Pay `onPaymentDataChanged`): every DIRECT pay.js-style
 * Google Pay integration exposes the raw `onPaymentDataChanged` callback, so we
 * can recompute tax from the postal code Google hands us mid-sheet and patch
 * the total **inside the open sheet** via the returned `newTransactionInfo` —
 * the only pre-authorization channel for the buyer's ZIP (billing is never
 * exposed before auth). Used by BOTH direct integrations that share this sheet
 * mechanic: Adyen Google Pay (Adyen forwards Google's callback verbatim) and
 * the direct Stripe Google Pay form (`checkout_stripe_direct_google_pay`).
 * `provider` only labels RUM logs — the sheet contract is Google's, not the
 * PSP's.
 *
 * Google's redacted intermediate address gives 5 digits for the US and only the
 * first 3 characters for CA/UK, so we send the value straight to the tax endpoint
 * and let the BE decide validity instead of regex-gating a deliberately partial
 * value. The result is stashed in `capturedWalletTaxRef` so the finalize step can reuse
 * the resolved ZIP. We intentionally do NOT write to checkout context here:
 * wallet hosts re-create their component on `pricing` changes, and mutating
 * context mid-sheet would unmount the live Google Pay sheet.
 */
export const useGooglePayShippingZipTax = ({ provider }: { provider: 'adyen' | 'stripe' }) => {
  const { customer, pricing, featureFlags, sessionStartedWithoutValidTax } = useContext(CheckoutContext);
  const { runWalletZipTax, capturedWalletTaxRef, applyWalletTaxToCheckout } = useWalletZipTaxCalculator();
  // Mirror `useStripeWalletShippingZipTax`: pair the callback's presence with
  // the composite gate so a non-US/CA mount OR an FF-OFF mount that also
  // started with a valid tax (ACDEV-27914) hands the SDK NO
  // `onPaymentDataChanged` at all — no closure retained for an event the SDK
  // will never fire (the request builders won't set
  // `callbackIntents: ['SHIPPING_ADDRESS']` or `shippingAddressRequired` for
  // these sessions either).
  const shouldCollectZipForTax = shouldUseWalletDerivedZipTax(customer, featureFlags, sessionStartedWithoutValidTax);

  // Session country is used only for `TransactionInfo.countryCode` (Google Pay's
  // merchant-processing-country field). Wallet country — used for ZIP
  // normalization — is handled inside `runWalletZipTax` off the shipping
  // address Google hands us, since it may differ from the session country.
  const sessionCountry = customer.taxCountry ?? customer.geoLocator?.countryCode2 ?? '';

  const onPaymentDataChanged = useCallback<NonNullable<OnPaymentDataChanged>>(
    async (intermediatePaymentData) => {
      const startedAt = Date.now();
      const slowWarnTimer = setTimeout(() => {
        logWarn(InfoLogs.walletZipTaxSlow, {
          provider,
          wallet: 'google_pay',
          elapsedMs: Date.now() - startedAt,
          walletCountry: intermediatePaymentData.shippingAddress?.countryCode?.toUpperCase() ?? '',
        });
      }, ZIP_TAX_SLOW_WARN_MS);
      // Belt-and-suspenders outer try/catch: this callback's return value IS
      // the sheet resolve, so a throw would leave Google Pay stuck on
      // "loading". Catch → log via RUM → return `{}` so the sheet keeps the
      // current total and the buyer can still complete payment.
      try {
        // Google Pay uses this callback's RETURN VALUE as the sheet
        // resolve (unlike Stripe ECE / Adyen Apple Pay which have a separate
        // `resolve` fn), so we cannot resolve early and keep computing —
        // returning IS resolving. The BE tax call is mandatory (jurisdiction
        // logging / compliance runs on the BE regardless of wallet country),
        // so we always await `runWalletZipTax` and only resolve once the
        // response lands.
        const outcome = await runWalletZipTax(
          intermediatePaymentData.shippingAddress?.postalCode,
          intermediatePaymentData.shippingAddress?.countryCode
        );
        if (outcome.status === WalletZipTaxStatus.SUCCESS) {
          return {
            newTransactionInfo: buildGooglePayTaxTransactionInfo(
              outcome.result.priceBreakdown,
              pricing,
              sessionCountry
            ),
          };
        }
        // `runWalletZipTax` already logs `ERROR` outcomes to RUM via
        // `ErrorLogs.walletZipTaxError` — no double-logging here.

        // `same_zip`, `unchanged`, `skipped`, `invalid`, `error`: return `{}` so
        // the sheet keeps the current total and stops spinning.
        return {};
      } catch (error) {
        logError(ErrorLogs.walletZipTaxHandlerError, error, {
          provider,
          wallet: 'google_pay',
          walletCountry: intermediatePaymentData.shippingAddress?.countryCode?.toUpperCase() ?? '',
        });
        return {};
      } finally {
        clearTimeout(slowWarnTimer);
      }
    },
    [runWalletZipTax, pricing, sessionCountry, provider]
  );

  return {
    onPaymentDataChanged: shouldCollectZipForTax ? onPaymentDataChanged : undefined,
    capturedWalletTaxRef,
    applyWalletTaxToCheckout,
  };
};
